import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { Contact } from "@/components/companies/contacts/types";

interface UseContactFormProps {
  companyId: string;
  onSuccess: (data: any) => void;
  contactId?: string;
}

export const useContactForm = ({ companyId, onSuccess, contactId }: UseContactFormProps) => {
  const [formData, setFormData] = useState<Partial<Contact>>({
    first_name: "",
    last_name: "",
    function: "",
    email: "",
    phone: "",
    notes: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState<string[]>([]);
  
  useEffect(() => {
    if (contactId) {
      fetchLinkedJobs();
    }
  }, [contactId]);
  
  const fetchLinkedJobs = async () => {
    if (!contactId) return;
    
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select('id')
        .eq('contact_id', contactId);
      
      if (error) throw error;
      
      if (data) {
        setSelectedJobs(data.map(job => job.id));
      }
    } catch (error) {
      console.error("Error fetching linked jobs:", error);
    }
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const resetForm = () => {
    setFormData({
      first_name: "",
      last_name: "",
      function: "",
      email: "",
      phone: "",
      notes: "",
    });
    setSelectedJobs([]);
  };

  const logCompanyHistory = async (action: string, description: string) => {
    try {
      let userName = "Current User";
      const { data: userSession } = await supabase.auth.getSession();
      if (userSession.session?.user) {
        const { data: userProfile } = await supabase
          .from('profiles')
          .select('first_name, last_name')
          .eq('id', userSession.session.user.id)
          .single();
        
        if (userProfile) {
          userName = `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() || "Current User";
        }
      }
      
      // NOTE: History tracking is now handled automatically by database triggers
      // No manual history logging needed here
    } catch (error) {
      console.error("Error logging company history:", error);
    }
  };
  
  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.first_name?.trim()) {
      toast({
        title: "Error",
        description: "First name is required",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      const name = `${formData.first_name} ${formData.last_name || ''}`.trim();
      
      const { data: userOrgData } = await supabase.auth.getSession();
      const userUuid = userOrgData.session?.user?.id;
      
      let organizationUuid = null;
      
      if (userUuid) {
        const { data: userOrg } = await supabase
          .from('user_organization')
          .select('organization_uuid')
          .eq('user_uuid', userUuid)
          .single();
          
        organizationUuid = userOrg?.organization_uuid;
      }
      
      const operation = contactId ? 'update' : 'insert';
      const query = contactId 
        ? supabase.from('contacts').update({
            first_name: formData.first_name,
            last_name: formData.last_name || null,
            name: name,
            function: formData.function || null,
            email: formData.email || null,
            phone: formData.phone || null,
            notes: formData.notes || null,
          }).eq('id', contactId)
        : supabase.from('contacts').insert({
            company_id: companyId,
            first_name: formData.first_name,
            last_name: formData.last_name || null,
            name: name,
            function: formData.function || null,
            email: formData.email || null,
            phone: formData.phone || null,
            notes: formData.notes || null,
            organization_uuid: organizationUuid,
          });
      
      const { data, error } = contactId 
        ? await query 
        : await query.select();
      
      if (error) throw error;
      
      await logCompanyHistory(
        contactId ? "Contact Updated" : "Contact Added",
        contactId 
          ? `Contact "${name}" was updated` 
          : `Contact "${name}" was added`
      );
      
      const createdContactId = contactId || (data && data[0] ? data[0].id : null);
      
      if (createdContactId && selectedJobs.length > 0) {
        if (contactId) {
          await supabase
            .from('jobs')
            .update({ contact_id: null })
            .eq('contact_id', contactId);
        }
        
        for (const jobId of selectedJobs) {
          await supabase
            .from('jobs')
            .update({ contact_id: createdContactId })
            .eq('id', jobId);
        }
      }
      
      toast({
        title: "Success",
        description: contactId ? "Contact updated successfully" : "Contact added successfully",
      });
      
      onSuccess(data);
      resetForm();
    } catch (error) {
      console.error(`Error ${contactId ? 'updating' : 'adding'} contact:`, error);
      toast({
        title: "Error",
        description: `Failed to ${contactId ? 'update' : 'add'} contact`,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return {
    formData,
    setFormData,
    isSubmitting,
    selectedJobs,
    setSelectedJobs,
    handleChange,
    submitForm,
    resetForm
  };
};
